import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BrainOrb, Button, Card, EmptyState, GlassPill, Skeleton } from '../components/primitives';
import { RosterSection } from '../components/RosterSection';
import { useActiveTeam } from '../lib/queries/useActiveTeam';
import { useTeamPlayers } from '../lib/queries/useTeamPlayers';
import { useCaptainQueue } from '../lib/queries/useCaptainQueue';
import { useCalibration } from '../contexts/CalibrationContext';
import { addPlayer } from '../lib/playerCrud';
import { useRegion } from '../lib/i18n/useRegion';
import './page-shell.css';
import './Players.css';

export function Players() {
  const navigate = useNavigate();
  const { team, loading: teamLoading } = useActiveTeam();
  const { level } = useCalibration();
  const { terms } = useRegion();
  const [refreshNonce, setRefreshNonce] = useState(0);
  const { rows: players, state, errorMessage } = useTeamPlayers(team?.id, teamLoading, {
    limit: 40,
    refreshKey: refreshNonce,
  });

  const [formOpen, setFormOpen] = useState(false);
  const [name, setName] = useState('');
  const [position, setPosition] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const closeForm = () => {
    setFormOpen(false);
    setName('');
    setPosition('');
    setMessage(null);
  };

  const submit = async () => {
    if (!team?.id || saving) return;
    setSaving(true);
    setMessage(null);
    try {
      const result = await addPlayer({ teamId: team.id, name, position: position || null });
      if (!result.ok) {
        setMessage(result.error);
        return;
      }
      setName('');
      setPosition('');
      setMessage(result.source === 'local' ? 'Added (saved on this device).' : 'Added.');
      setRefreshNonce((n) => n + 1);
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') void submit();
  };

  // Rotating-captain register (Ben item #7) — read + manage the queue
  // for this team. Resolves player names via the existing roster query.
  const { queue, enable: enableQueue, disable: disableQueue, saveQueue } = useCaptainQueue(team?.id);
  const queueByName = useMemo(() => {
    return queue.map((entry) => ({
      ...entry,
      name: players.find((p) => p.id === entry.playerId)?.name ?? null,
    }));
  }, [queue, players]);
  const captainEnabled = queue.length > 0;

  const moveCaptain = (idx: number, delta: -1 | 1) => {
    const target = idx + delta;
    if (target < 0 || target >= queue.length) return;
    const next = [...queue];
    [next[idx], next[target]] = [next[target], next[idx]];
    void saveQueue(next);
  };

  const handleEnableQueue = () => {
    if (players.length === 0) return;
    void enableQueue(players.map((p) => p.id));
  };

  return (
    <main className="main page">
      <header className="page-head reviews-head">
        <BrainOrb size="lg" ariaLabel="Brain" />
        <div>
          <span className="page-eyebrow">Layer 3 · player goals</span>
          <h1 className="page-title">Players</h1>
          <p className="page-sub">Set a goal in 30 seconds. Brain weighs every goal on every turn that follows.</p>
        </div>
        {team?.id && (
          <Button
            variant={formOpen ? 'glass' : 'primary'}
            size="touchline"
            onClick={() => (formOpen ? closeForm() : setFormOpen(true))}
            className="players-add-cta"
          >
            {formOpen ? 'Close' : '+ Add a player'}
          </Button>
        )}
      </header>

      {formOpen && team?.id && (
        <Card variant="opaque" className="players-add">
          <div className="players-add__row">
            <label className="players-add__field">
              <span>Name</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="e.g. Marco Adams"
                aria-label="Player name"
                autoFocus
              />
            </label>
            <label className="players-add__field players-add__field--pos">
              <span>Position</span>
              <input
                type="text"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="GK / CB / CM / ST"
                aria-label="Player position"
                maxLength={16}
              />
            </label>
            <Button
              variant="primary"
              size="touchline"
              onClick={() => void submit()}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
          {message && (
            <p className="players-add__msg" data-ok={!message.toLowerCase().includes('could') || undefined}>
              {message}
            </p>
          )}
        </Card>
      )}

      {state === 'loading' && (
        <ul className="players-list" aria-label="Loading players">
          {Array.from({ length: 6 }, (_, i) => (
            <li key={i}>
              <Card variant="opaque">
                <Skeleton variant="circle" />
                <div style={{ height: 6 }} />
                <Skeleton variant="text" width="60%" height={16} />
                <div style={{ height: 4 }} />
                <Skeleton variant="text" width="35%" height={12} />
              </Card>
            </li>
          ))}
        </ul>
      )}
      {state === 'error' && (
        <Card variant="opaque">
          <p className="page-empty">
            Couldn't load players.{errorMessage ? ` (${errorMessage})` : null}
          </p>
        </Card>
      )}
      {state === 'empty' && (
        <EmptyState
          glyph="🧑"
          title={team?.id ? `No players in the ${terms.roster} yet` : 'No team yet'}
          body={team?.id
            ? `Add a player above, or seed a demo ${terms.roster} from Settings to see how everything fits.`
            : 'Onboarding takes about 5 minutes — voice + a few taps and you\'ll have a usable team.'}
          cta={team?.id
            ? undefined
            : { label: 'Start onboarding', onClick: () => navigate('/onboarding') }}
          hint={team?.id && level === 'academy'
            ? <Link to="/settings">Seed demo players from Settings →</Link>
            : null}
        />
      )}
      {state === 'ok' && players.length > 0 && (
        <Card variant="opaque" className="captain-section">
          <div className="captain-section__head">
            <div>
              <span className="page-eyebrow">🧢 Rotating captain register</span>
              <p className="captain-section__sub">
                {captainEnabled
                  ? 'A different player captains each game. Brain surfaces "this week\'s captain" on game-day.'
                  : 'Build social leadership by giving every player a turn at captain.'}
              </p>
            </div>
            {captainEnabled ? (
              <Button variant="glass" size="touchline" onClick={() => void disableQueue()}>
                Turn off
              </Button>
            ) : (
              <Button variant="primary" size="touchline" onClick={handleEnableQueue}>
                Enable
              </Button>
            )}
          </div>
          {captainEnabled && (
            <ol className="captain-list">
              {queueByName.map((entry, idx) => (
                <li key={entry.playerId} className="captain-row" data-head={idx === 0 || undefined}>
                  <span className="captain-row__pos">{idx + 1}</span>
                  <span className="captain-row__name">
                    {entry.name ?? <em>(unknown player — left the team?)</em>}
                  </span>
                  {idx === 0 && <GlassPill mono>Next up</GlassPill>}
                  {entry.lastCaptainedAt && (
                    <span className="captain-row__last">
                      Last: {new Date(entry.lastCaptainedAt).toLocaleDateString()}
                    </span>
                  )}
                  <div className="captain-row__actions">
                    <button
                      type="button"
                      className="captain-row__btn"
                      onClick={() => moveCaptain(idx, -1)}
                      disabled={idx === 0}
                      aria-label="Move up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="captain-row__btn"
                      onClick={() => moveCaptain(idx, 1)}
                      disabled={idx === queueByName.length - 1}
                      aria-label="Move down"
                    >
                      ↓
                    </button>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>
      )}

      {state === 'ok' && players.length > 0 && (
        <ul className="player-grid">
          {players.map((p) => (
            <li key={p.id}>
              <Link to={`/players/${p.id}`} className="player-card">
                <Card variant="opaque">
                  <div className="player-card__name">{p.name}</div>
                  {p.position && <div className="player-card__pos">{p.position}</div>}
                </Card>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <RosterSection teamId={team?.id} teamLoading={teamLoading} />
    </main>
  );
}
